import type { ZoneGeoJsonPoint, ZoneGeoJsonPolygon } from '@chukua/common';
import { Map as MapLibreMap } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { TerraDraw, TerraDrawPointMode, TerraDrawPolygonMode, TerraDrawSelectMode } from 'terra-draw';
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter';
import { configureMapLibreWorker } from '../../lib/configure-maplibre-worker';
import { polygonCentroid } from './polygon-centroid';

export interface ZoneMapValue {
  polygon: ZoneGeoJsonPolygon | null;
  centroid: ZoneGeoJsonPoint | null;
}

export type ZoneMapDrawMode = 'polygon' | 'point';

interface UseZoneMapEditorArgs {
  /** Real MapTiler tiles, or null when unconfigured — see ZoneMapEditor's fallback. */
  tileKey: string | null;
  value: ZoneMapValue;
  onChange: (value: ZoneMapValue) => void;
}

/** Nairobi — the centre of Index Cargo's corridor (#21). */
const DEFAULT_CENTER: [number, number] = [36.8219, -1.2921];
const DEFAULT_ZOOM = 6;

function initialFeatures(value: ZoneMapValue) {
  const features = [];
  if (value.polygon) features.push({ type: 'Feature' as const, geometry: value.polygon, properties: {} });
  if (value.centroid) features.push({ type: 'Feature' as const, geometry: value.centroid, properties: {} });
  return features;
}

interface MountZoneMapArgs {
  container: HTMLDivElement;
  tileKey: string;
  /** Read at 'load', not at mount, so an edit's saved shapes are the latest value. */
  initialValue: () => ZoneMapValue;
  onChange: (value: ZoneMapValue) => void;
  onReady: () => void;
}

/** Creates the map and its terra-draw layer; returns the draw instance and the teardown. */
function mountZoneMap({ container, tileKey, initialValue, onChange, onReady }: MountZoneMapArgs) {
  configureMapLibreWorker();
  const map = new MapLibreMap({
    container,
    style: `https://api.maptiler.com/maps/streets/style.json?key=${tileKey}`,
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
  });
  const adapter = new TerraDrawMapLibreGLAdapter({ map });
  const draw = new TerraDraw({ adapter, modes: [new TerraDrawPolygonMode(), new TerraDrawPointMode(), new TerraDrawSelectMode()] });

  function applySnapshot() {
    const features = draw.getSnapshot();
    const polygon = (features.find((f) => f.geometry.type === 'Polygon')?.geometry as ZoneGeoJsonPolygon | undefined) ?? null;
    const point = (features.find((f) => f.geometry.type === 'Point')?.geometry as ZoneGeoJsonPoint | undefined) ?? null;
    onChange({ polygon, centroid: polygon ? polygonCentroid(polygon) : point });
  }

  draw.on('finish', applySnapshot);
  draw.on('change', applySnapshot);
  map.on('load', () => {
    draw.start();
    draw.setMode('polygon');
    const initial = initialFeatures(initialValue());
    if (initial.length > 0) draw.addFeatures(initial);
    onReady();
  });

  // The container follows the page layout and viewport height — MapLibre otherwise keeps rendering at its stale canvas size.
  const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => map.resize());
  resizeObserver?.observe(container);

  function destroy() {
    resizeObserver?.disconnect();
    draw.stop();
    map.remove();
  }

  return { draw, destroy };
}

/**
 * Owns the MapLibre GL map and terra-draw instance for the zone polygon/centroid
 * editor. A finished or edited polygon derives its centroid automatically
 * (polygonCentroid); point mode sets a centroid independently, for a named
 * point with no polygon (gitaumoses4/chukua#21) — both stay optional.
 */
export function useZoneMapEditor({ tileKey, value, onChange }: UseZoneMapEditorArgs) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const drawRef = useRef<TerraDraw | null>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const [drawMode, setDrawModeState] = useState<ZoneMapDrawMode>('polygon');
  // terra-draw throws if a mode is set before the map's 'load' starts it, so the draw controls wait for this.
  const [isMapReady, setIsMapReady] = useState(false);

  useEffect(() => {
    valueRef.current = value;
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !tileKey) return;

    const { draw, destroy } = mountZoneMap({
      container,
      tileKey,
      initialValue: () => valueRef.current,
      onChange: (next) => onChangeRef.current(next),
      onReady: () => setIsMapReady(true),
    });
    drawRef.current = draw;

    return () => {
      destroy();
      drawRef.current = null;
      setIsMapReady(false);
    };
  }, [tileKey]);

  function setDrawMode(mode: ZoneMapDrawMode) {
    setDrawModeState(mode);
    if (isMapReady) drawRef.current?.setMode(mode);
  }

  function clearPolygon() {
    onChange({ polygon: null, centroid: value.centroid });
  }

  function clearCentroid() {
    onChange({ polygon: value.polygon, centroid: null });
  }

  return { containerRef, isMapReady, drawMode, setDrawMode, clearPolygon, clearCentroid };
}
